import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  DialogActions,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { string } from 'yup';
import IconSuccess from '../../../../assets/icon-success.svg';
import DetailsCard from '../../../../components/DetailsCard/DetailsCard';
import FormatPhoneInput from '../../../../components/FormatNumber/FormatPhoneInput';
import DefaultModal from '../../../../components/Modals/DefaultModal';
import PageTitle from '../../../../components/PageTitle/PageTitle';
import { Company } from '../../../../models/company';
import {
  ADMINISTRATION_COMPANIES,
  ADMINISTRATION_COMPANIES_CREATE
} from '../../../../router/constants';
import { companyServices } from '../../../../services/companyServices';
import { removeEmptyObjectFields } from '../../../../utils/helpers';
import FormatNumberInput from '../../../../components/FormatNumber/FormatNumberInput';

const companySchema = yup.object({
  name: string().default('').required('Name is required'),
  glExpenseAccount: string().default(''),
  freelancer: string().default('').required('Freelancers is required'),
  status: string().default('').required('Status is required'),
  phone: string().default('').required('Phone Number is required'),
  markup: string()
    .default('')
    .typeError('Markup is required')
    .required('Markup is required'),
  contactEmail: string()
    .default('')
    .email('Primary Email must be a valid email')
    .required('Primary Email is required')
});

type CreateCompanyForm = yup.InferType<typeof companySchema>;

const defaultValues = companySchema.getDefault();

const CreateCompany = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateCompanyForm>({
    defaultValues,
    resolver: yupResolver(companySchema)
  });

  useEffect(() => {
    if (id) {
      companyServices.getCompanyById(Number(id)).then((response) => {
        const data = removeEmptyObjectFields(response.data);
        reset(data as unknown as CreateCompanyForm);
      });
    }
  }, [id]);

  const createCompany = (company: Company) => {
    companyServices.createCompany(company).then(() => {
      setSuccessModal(true);
    });
  };

  const updateCompany = (company: Company) => {
    companyServices.updateCompany(company).then(() => {
      setSuccessModal(true);
    });
  };

  const closeSuccessModal = () => {
    reset(defaultValues);
    setSuccessModal(false);
    navigate(ADMINISTRATION_COMPANIES_CREATE);
  };

  const onSubmit = (data: CreateCompanyForm) => {
    const company = data as unknown as Company;
    id ? updateCompany(company) : createCompany(company);
  };

  return (
    <>
      <PageTitle>{id ? 'Edit Company' : 'Add New Company'}</PageTitle>
      <DetailsCard title={id ? 'Edit Company' : 'New Company'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company *"
                    variant="outlined"
                    error={!!errors.name}
                    helperText={errors.name ? errors.name?.message : ''}
                  />
                )}
              />
              <Controller
                name="glExpenseAccount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="GL Expense Account"
                    variant="outlined"
                    error={!!errors.glExpenseAccount}
                    helperText={
                      errors.glExpenseAccount
                        ? errors.glExpenseAccount?.message
                        : ''
                    }
                  />
                )}
              />
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Status *"
                    error={!!errors.status}
                    helperText={errors.status?.message}
                  >
                    <MenuItem value={'ACTIVE'}>Active</MenuItem>
                    <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="freelancer"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Freelancers *"
                    error={!!errors.freelancer}
                    helperText={errors.freelancer?.message}
                  >
                    <MenuItem value={'W2'}>W2</MenuItem>
                    <MenuItem value={'_1099'}>1099</MenuItem>
                    <MenuItem value={'BOTH'}>Both</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="contactEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="email"
                    label="Contact Email *"
                    variant="outlined"
                    error={!!errors.contactEmail}
                    helperText={
                      errors.contactEmail ? errors.contactEmail?.message : ''
                    }
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Contact Phone *"
                    error={!!errors.phone}
                    helperText={errors.phone ? errors.phone?.message : ''}
                    InputProps={{
                      // eslint-disable-next-line
                      inputComponent: FormatPhoneInput as any
                    }}
                  />
                )}
              />
              <Controller
                name="markup"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Markup *"
                    error={!!errors.markup}
                    helperText={errors.markup ? errors.markup?.message : ''}
                    InputProps={{
                      // eslint-disable-next-line
                      inputComponent: FormatNumberInput as any,
                      inputProps: {
                        suffix: '%',
                        // eslint-disable-next-line
                        isAllowed: ({ floatValue }: any) => floatValue <= 100
                      }
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Button type="submit" sx={{ width: 150 }}>
              Save
            </Button>
          </Box>
        </form>
      </DetailsCard>
      <DefaultModal open={successModal} handleClose={closeSuccessModal}>
        <Box sx={{ px: 6, pb: 6 }}>
          <Box textAlign="center" mb={3}>
            <img src={IconSuccess} alt="success" />
          </Box>
          <Typography variant="h3" textAlign="center" color="grey.900" mb={2}>
            Success!
          </Typography>
          <Typography textAlign="center">
            {id
              ? 'You have successfully updated Company'
              : 'You have successfully added new Company'}
          </Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(ADMINISTRATION_COMPANIES)}
            sx={{ mr: 4 }}
          >
            View All Companies
          </Button>
          <Button onClick={closeSuccessModal}>Create New Company</Button>
        </DialogActions>
      </DefaultModal>
    </>
  );
};

export default CreateCompany;
